import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Card, Button, Form, InputGroup, ListGroup, Spinner, Badge } from 'react-bootstrap';
import { getWhatsappStaff, addWhatsappStaff, updateWhatsappStaffAlerts, removeWhatsappStaff } from '../../actions/whatsappAccess';
import { LoadingState, ErrorBanner, EmptyState } from './StateViews';

const PHONE_LENGTH = 10;

const StaffRecipientsPanel = ({ getWhatsappStaff, addWhatsappStaff, updateWhatsappStaffAlerts, removeWhatsappStaff }) => {
    const [rows, setRows] = useState(null);
    const [alertOptions, setAlertOptions] = useState({});
    const [saving, setSaving] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [phone, setPhone] = useState('');
    const [name, setName] = useState('');
    const [adding, setAdding] = useState(false);
    const [removing, setRemoving] = useState(null);

    const load = useCallback(async () => {
        setLoadFailed(false);
        const data = await getWhatsappStaff();
        if (!data) return setLoadFailed(true);
        setRows(data.staff);
        setAlertOptions(data.alertOptions);
    }, [getWhatsappStaff]);

    useEffect(() => {
        load();
    }, [load]);

    const digits = phone.replace(/\D/g, '').slice(-PHONE_LENGTH);
    const canAdd = digits.length === PHONE_LENGTH && !adding;

    const add = async (event) => {
        event.preventDefault();
        if (!canAdd) return;
        setAdding(true);
        const result = await addWhatsappStaff({ phone: digits, name });
        setAdding(false);
        if (!result) return;
        setRows((current) => [...current, { alerts: [], ...result }]);
        setPhone('');
        setName('');
    };

    const remove = async (target) => {
        setRemoving(target);
        const result = await removeWhatsappStaff(target);
        setRemoving(null);
        if (!result) return;
        setRows((current) => current.filter((row) => row.phone !== target));
    };

    const toggleAlert = async (row, key) => {
        const held = row.alerts || [];
        const next = held.includes(key) ? held.filter((item) => item !== key) : [...held, key];
        setSaving(row.phone);
        const result = await updateWhatsappStaffAlerts(row.phone, next);
        setSaving(null);
        if (!result) return;
        setRows((current) => current.map((item) => (item.phone === row.phone ? { ...item, alerts: result.alerts } : item)));
    };

    return (
        <Card className="shadow-sm h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
                <span className="fw-semibold">Staff alert recipients</span>
                {rows && <Badge bg="secondary">{rows.length}</Badge>}
            </Card.Header>
            <Card.Body>
                <div className="text-muted small mb-3">
                    Tick the alerts each person receives: new delivery orders, can returns, support requests and sales requests. A person with nothing ticked receives all of them.
                </div>
                <Form onSubmit={add} className="mb-3">
                    <InputGroup>
                        <Form.Control
                            aria-label="Staff name"
                            placeholder="Name (optional)"
                            maxLength={60}
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                        <Form.Control
                            aria-label="Staff phone"
                            placeholder="10 digit phone"
                            inputMode="numeric"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                        />
                        <Button type="submit" variant="primary" disabled={!canAdd}>
                            {adding ? <Spinner size="sm" animation="border" /> : 'Add'}
                        </Button>
                    </InputGroup>
                </Form>
                {loadFailed && <ErrorBanner message="Failed to load the staff numbers" onRetry={load} />}
                {!loadFailed && !rows && <LoadingState />}
                {rows && rows.length === 0 && <EmptyState className="mb-0">No staff numbers, so nobody receives the internal updates.</EmptyState>}
            </Card.Body>
            {rows && rows.length > 0 && (
                <ListGroup variant="flush">
                    {rows.map((row) => (
                        <ListGroup.Item key={row.phone}>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>
                                    <span className="fw-semibold">{row.name || 'Unnamed'}</span>
                                    <span className="text-muted ms-2">{row.phone}</span>
                                </span>
                                <Button size="sm" variant="outline-danger" disabled={removing === row.phone} onClick={() => remove(row.phone)}>
                                    {removing === row.phone ? <Spinner size="sm" animation="border" /> : 'Remove'}
                                </Button>
                            </div>
                            <div className="d-flex flex-wrap gap-3 mt-2">
                                {Object.entries(alertOptions).map(([key, label]) => (
                                    <Form.Check
                                        key={key}
                                        type="checkbox"
                                        id={`alert-${row.phone}-${key}`}
                                        label={label}
                                        checked={(row.alerts || []).includes(key)}
                                        disabled={saving === row.phone}
                                        onChange={() => toggleAlert(row, key)}
                                    />
                                ))}
                            </div>
                            {(!row.alerts || row.alerts.length === 0) && <div className="text-muted small mt-1">Receives all alerts</div>}
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </Card>
    );
};

StaffRecipientsPanel.propTypes = {
    getWhatsappStaff: PropTypes.func.isRequired,
    addWhatsappStaff: PropTypes.func.isRequired,
    updateWhatsappStaffAlerts: PropTypes.func.isRequired,
    removeWhatsappStaff: PropTypes.func.isRequired
};

export default connect(null, { getWhatsappStaff, addWhatsappStaff, updateWhatsappStaffAlerts, removeWhatsappStaff })(StaffRecipientsPanel);
